import { Component } from '@theme/component';
import { DialogComponent } from '@theme/dialog';
import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * The quick view trigger that sits on a product card.
 *
 * Follows the same approach as quick add: fetch the product page, then hand its
 * `[data-product-grid-content]` to a single shared panel. The panel is opened before
 * the fetch resolves so the shopper gets feedback straight away, and the fetched page
 * is cached per card so re-opening is instant.
 *
 * @extends {Component}
 */
class QuickViewComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {Document | null} */
  #cachedProductHtml = null;

  /** The product page this card points at. */
  get productPageUrl() {
    const link = this.closest('product-card')?.querySelector('a[ref="productCardLink"]');

    return link instanceof HTMLAnchorElement ? link.href : null;
  }

  /** The one panel shared by every card on the page. */
  get panel() {
    const panel = document.getElementById('quick-view-panel');

    return panel instanceof QuickViewPanel ? panel : null;
  }

  /**
   * Warms the cache before the click lands. Wired to pointerenter and focusin on the
   * trigger, the same way the product card warms quick add.
   */
  prefetch = () => {
    if (this.#cachedProductHtml) return;

    this.#fetchProductPage();
  };

  /**
   * Opens the panel for this card's product.
   * @param {Event} event - The click event.
   */
  open = async (event) => {
    event.preventDefault();

    const { panel, productPageUrl } = this;

    if (!panel || !productPageUrl) return;

    panel.prepare(productPageUrl, this.dataset.productTitle ?? '');

    if (!this.#cachedProductHtml) await this.#fetchProductPage();

    if (!this.#cachedProductHtml) {
      panel.fail(productPageUrl);
      return;
    }

    // Fill from a copy. The panel morphs whatever it is handed, and the cache has to
    // survive the card being opened more than once.
    const copy = new DOMParser().parseFromString(this.#cachedProductHtml.documentElement.outerHTML, 'text/html');

    panel.fill(copy, productPageUrl);
  };

  /**
   * Fetches and caches this card's product page.
   * @returns {Promise<void>}
   */
  async #fetchProductPage() {
    const url = this.productPageUrl;

    if (!url) return;

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#abortController.signal });

      if (!response.ok) throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);

      this.#cachedProductHtml = new DOMParser().parseFromString(await response.text(), 'text/html');
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;

      console.error(error);
    } finally {
      this.#abortController = null;
    }
  }
}

if (!customElements.get('quick-view-component')) {
  customElements.define('quick-view-component', QuickViewComponent);
}

/**
 * The shared quick view panel — a drawer on desktop, a sheet on smaller screens.
 *
 * Refs: `dialog` (the dialog element), `panelContent` (where the product page's
 * content is morphed in), `detailsLink` (the link through to the full product page)
 * and the optional `panelTitle` shown in the panel header.
 */
class QuickViewPanel extends DialogComponent {
  requiredRefs = ['dialog', 'panelContent', 'detailsLink'];

  /**
   * The product the panel is currently showing. Fetches that resolve after the
   * shopper has moved on to another card are discarded rather than shown.
   * @type {string | null}
   */
  #currentUrl = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
  }

  /**
   * Opens the panel empty, pointed at a product.
   * @param {string} url - The product page URL.
   * @param {string} title - The product title, for the panel header.
   */
  prepare(url, title) {
    const { panelContent, detailsLink } = this.refs;

    this.#currentUrl = url;
    detailsLink.href = url;
    this.#setTitle(title, url);

    // Clear the previous product first, so nothing stale is ever on screen.
    panelContent.replaceChildren();
    this.dataset.state = 'loading';

    this.showDialog();
  }

  /**
   * Sets the product name in the panel header, which doubles as a route through to
   * the product page.
   * @param {string} title - The product title.
   * @param {string} url - The product page URL.
   */
  #setTitle(title, url) {
    const { panelTitle } = this.refs;

    if (!panelTitle) return;

    if (title) panelTitle.textContent = title;
    if (panelTitle instanceof HTMLAnchorElement) panelTitle.href = url;
  }

  /**
   * Fills the panel with a fetched product page.
   * @param {Document} html - The fetched product page.
   * @param {string} url - The page it was fetched from.
   */
  fill(html, url) {
    if (url !== this.#currentUrl) return;

    const content = html.querySelector('[data-product-grid-content]');

    if (!content) {
      this.fail(url);
      return;
    }

    morph(this.refs.panelContent, content);
    this.dataset.state = 'ready';
  }

  /**
   * Falls back to a link through to the product page when the fetch fails.
   * @param {string} url - The page that could not be shown.
   */
  fail(url) {
    if (url !== this.#currentUrl) return;

    this.dataset.state = 'error';
  }

  /**
   * Closes the panel once the item is in the cart, so the cart drawer is not opening
   * behind it. Matches how the quick add modal behaves.
   * @param {CustomEvent} event - The cart update event.
   */
  #handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;

    this.closeDialog();
  };

  /**
   * Keeps the details link on the variant the shopper is actually looking at.
   * @param {CustomEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const anchor = event.detail.data.html?.querySelector('.view-product-title a');

    if (!(anchor instanceof HTMLAnchorElement)) return;

    this.refs.detailsLink.href = anchor.href;
    this.#setTitle('', anchor.href);
    this.#currentUrl = anchor.href;
  };
}

if (!customElements.get('quick-view-panel')) {
  customElements.define('quick-view-panel', QuickViewPanel);
}
